import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getCaseStoreScopeInfo } from "src/pages/UserDashboard/Cases/api/casesApi";
import { ServerStream, type WorkPacket } from "src/types/work-packets";
import type { CaseType } from "../Cases/case-types";
import { useAccountScopeDeactivationDatesMap } from "../WorkPacketsGrid/filters/hooks/useAccountOptions";
import type { ActivationStatus } from "./activation-status-types";
import { evaluateActivationStatusForStream, evaluateScopeActivationStatus } from "./activation-status-utils";

// allow undefined work packet so that this hook can be used while work packet is being fetched

const fallbackWorkPacket = { storeId: -1, recoveryStreamServer: ServerStream.CHARGEBACKS };

export function useFetchWorkPacketActivationStatus(workPacket: WorkPacket | undefined): ActivationStatus {
  const accountsScopeInfoMap = useAccountScopeDeactivationDatesMap();
  const { storeId, recoveryStreamServer } = workPacket ?? fallbackWorkPacket;

  const info = useMemo(
    () => evaluateActivationStatusForStream(accountsScopeInfoMap.get(storeId), recoveryStreamServer),
    [accountsScopeInfoMap, storeId, recoveryStreamServer],
  );

  return info;
}

export function useFetchCaseActivationStatus(caseId: string, caseType: CaseType): ActivationStatus {
  const { data } = useQuery({
    queryKey: ["cases", caseType, caseId, "store-scope-info"] as const,
    queryFn: ({ queryKey: [, caseType, caseId] }) => getCaseStoreScopeInfo(caseId, caseType),
  });

  const result = useMemo(() => evaluateScopeActivationStatus(data), [data]);

  return result;
}
